import { Trans } from "@lingui/macro";
import cx from "classnames";
import { curveCardinal } from "d3";
import React, { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import { Area, AreaChart } from "recharts";

import { useSettings } from "context/SettingsContext/SettingsContextProvider";
import { useTokensData } from "context/SyntheticsStateContext/hooks/globalsHooks";
import { selectChainId, selectGlvAndMarketsInfoData } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import {
  MarketTokensAPRData,
  getGlvDisplayName,
  getGlvOrMarketAddress,
  getMarketBadge,
  getMarketIndexName,
  getMarketPoolName,
} from "domain/synthetics/markets";
import { isGlvInfo } from "domain/synthetics/markets/glv";
import { PerformanceSnapshot, formatPerformanceBps } from "domain/synthetics/markets/performance";
import { useDaysConsideredInMarketsApr } from "domain/synthetics/markets/useDaysConsideredInMarketsApr";
import { PerformanceData, PerformanceSnapshotsData } from "domain/synthetics/markets/useGmGlvPerformance";
import { useUserEarnings } from "domain/synthetics/markets/useUserEarnings";
import { TokenData, convertToUsd, getTokenData } from "domain/synthetics/tokens";
import { EMPTY_ARRAY, getByKey } from "lib/objects";
import { usePoolsIsMobilePage } from "pages/Pools/usePoolsIsMobilePage";
import { getNormalizedTokenSymbol } from "sdk/configs/tokens";

import { AmountWithUsdHuman } from "components/AmountWithUsd/AmountWithUsd";
import { AprInfo } from "components/AprInfo/AprInfo";
import Button from "components/Button/Button";
import FavoriteStar from "components/FavoriteStar/FavoriteStar";
import { TableTd, TableTr } from "components/Table/Table";
import TokenIcon from "components/TokenIcon/TokenIcon";

import { GmTokensBalanceInfo } from "./GmTokensTotalBalanceInfo";
import GmAssetDropdown from "../GmAssetDropdown/GmAssetDropdown";
import { SyntheticsInfoRow } from "../SyntheticsInfoRow";
import { FeeApyLabel } from "./FeeApyLabel";
import { PerformanceLabel } from "./PerformanceLabel";

export const tokenAddressStyle = { fontSize: 5 };

export function GmListItem({
  token,
  marketsTokensApyData,
  marketsTokensIncentiveAprData,
  glvTokensIncentiveAprData,
  marketsTokensLidoAprData,
  glvTokensApyData,
  isFavorite,
  onFavoriteClick,
  glvPerformance,
  gmPerformance,
  glvPerformanceSnapshots,
  gmPerformanceSnapshots,
  isGraphLoading,
}: {
  token: TokenData;
  marketsTokensApyData: MarketTokensAPRData | undefined;
  marketsTokensIncentiveAprData: MarketTokensAPRData | undefined;
  glvTokensIncentiveAprData: MarketTokensAPRData | undefined;
  marketsTokensLidoAprData: MarketTokensAPRData | undefined;
  glvTokensApyData: MarketTokensAPRData | undefined;
  isFavorite: boolean | undefined;
  onFavoriteClick: ((address: string) => void) | undefined;
  glvPerformance?: PerformanceData | undefined;
  gmPerformance?: PerformanceData | undefined;
  glvPerformanceSnapshots?: PerformanceSnapshotsData | undefined;
  gmPerformanceSnapshots?: PerformanceSnapshotsData | undefined;
  isGraphLoading?: boolean;
}) {
  const chainId = useSelector(selectChainId);
  const marketsInfoData = useSelector(selectGlvAndMarketsInfoData);
  const tokensData = useTokensData();
  const userEarnings = useUserEarnings(chainId);
  const daysConsidered = useDaysConsideredInMarketsApr();
  const { showDebugValues } = useSettings();

  const marketOrGlv = getByKey(marketsInfoData, token?.address);

  const isGlv = isGlvInfo(marketOrGlv);

  const indexToken = isGlv ? marketOrGlv.glvToken : getTokenData(tokensData, marketOrGlv?.indexTokenAddress, "native");
  const longToken = getTokenData(tokensData, marketOrGlv?.longTokenAddress);
  const shortToken = getTokenData(tokensData, marketOrGlv?.shortTokenAddress);

  const marketOrGlvTokenAddress = marketOrGlv && getGlvOrMarketAddress(marketOrGlv);

  const apy = isGlv
    ? getByKey(glvTokensApyData, marketOrGlvTokenAddress)
    : getByKey(marketsTokensApyData, token?.address);
  const incentiveApr = isGlv
    ? getByKey(glvTokensIncentiveAprData, token?.address)
    : getByKey(marketsTokensIncentiveAprData, token?.address);
  const lidoApr = getByKey(marketsTokensLidoAprData, token?.address);
  const marketEarnings = getByKey(userEarnings?.byMarketAddress, token?.address);

  const isMobile = usePoolsIsMobilePage();

  if (!token || !indexToken || !longToken || !shortToken || !marketOrGlv) {
    return null;
  }

  const totalSupply = token?.totalSupply;
  const totalSupplyUsd = convertToUsd(totalSupply, token?.decimals, token?.prices?.minPrice);
  const tokenIconName = marketOrGlv?.isSpotOnly
    ? getNormalizedTokenSymbol(longToken.symbol) + getNormalizedTokenSymbol(shortToken.symbol)
    : getNormalizedTokenSymbol(indexToken.symbol);
  const poolName = getMarketPoolName({ longToken, shortToken });
  const compactPoolName = poolName.replace(/\s*\/\s*/g, "/");
  const marketIndexName = getMarketIndexName({ indexToken, isSpotOnly: Boolean(marketOrGlv?.isSpotOnly) });
  const primaryLabel = isGlv ? getGlvDisplayName(marketOrGlv) : compactPoolName;
  const secondaryLabel = isGlv ? marketIndexName : undefined;

  const tokenIconBadge = getMarketBadge(chainId, marketOrGlv);

  const handleFavoriteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!marketOrGlvTokenAddress) return;
    onFavoriteClick?.(marketOrGlvTokenAddress);
  };

  const performance = isGlv ? glvPerformance?.[token.address] : gmPerformance?.[token.address];
  const performanceSnapshots = isGlv
    ? glvPerformanceSnapshots?.[token.address]
    : gmPerformanceSnapshots?.[token.address];

  if (isMobile) {
    return (
      <div className="flex flex-col rounded-12 bg-gray-dark p-14">
        <div className="flex flex-wrap items-center">
          {onFavoriteClick && (
            <div className="cursor-pointer self-center" onClick={handleFavoriteClick}>
              <FavoriteStar isFavorite={isFavorite} />
            </div>
          )}
          <div className="ml-14 flex items-center">
            <div className="mr-21 flex shrink-0 items-center">
              <TokenIcon
                symbol={tokenIconName}
                displaySize={32}
                importSize={40}
                badge={tokenIconBadge}
                className="min-h-32 min-w-32"
              />
            </div>
            <div className="flex flex-col">
              <div className="flex text-16 font-medium leading-[1.5] text-white">
                <span className="inline-block max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap">
                  {primaryLabel}
                </span>

                {marketOrGlv.isDisabled && (
                  <span className="bg-orange text-10 ml-4 mt-8 inline-flex rounded-4 px-4 py-2 font-bold uppercase tabular-nums tracking-wide text-white">
                    <Trans>Disabled</Trans>
                  </span>
                )}

                <div className="inline-block">
                  <GmAssetDropdown token={token} marketsInfoData={marketsInfoData} tokensData={tokensData} />
                </div>
              </div>
              {secondaryLabel && (
                <div className="text-12 font-medium tracking-normal text-gray-lighter">{secondaryLabel}</div>
              )}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-8">
            <SnapshotGraph
              performanceSnapshots={performanceSnapshots ?? EMPTY_ARRAY}
              performance={performance ?? 0}
              isLoading={isGraphLoading}
            />
          </div>
        </div>

        <div className="mt-22 flex flex-col gap-13">
          <SyntheticsInfoRow
            label={<Trans>TVL</Trans>}
            value={
              <AmountWithUsdHuman
                amount={totalSupply}
                decimals={token.decimals}
                usd={totalSupplyUsd}
                symbol={token.symbol}
                usdOnTop
              />
            }
          />
          <SyntheticsInfoRow
            label={<Trans>Wallet</Trans>}
            value={
              <GmTokensBalanceInfo
                token={token}
                daysConsidered={daysConsidered}
                earnedRecently={marketEarnings?.recent}
                earnedTotal={marketEarnings?.total}
                isGlv={isGlv}
                singleLine={true}
              />
            }
          />
          <SyntheticsInfoRow
            label={<FeeApyLabel />}
            value={<AprInfo apy={apy} incentiveApr={incentiveApr} lidoApr={lidoApr} marketAddress={token.address} />}
          />
          <SyntheticsInfoRow
            label={<PerformanceLabel />}
            value={performance !== undefined ? formatPerformanceBps(performance) : "..."}
          />
        </div>
        <Button
          className="mt-11 w-max !px-21 !py-7 text-center"
          to={`/pools/details?market=${marketOrGlvTokenAddress}`}
          variant="tertiary"
        >
          <Trans>Pool Details</Trans>
        </Button>
      </div>
    );
  }

  return (
    <TableTr key={token.address} bordered={false} hoverable={false}>
      <TableTd className="!pl-0">
        <div className="w-[220px]">
          <div className="flex items-center">
            {onFavoriteClick && (
              <div
                className="mr-14 cursor-pointer self-center rounded-4 p-4 hover:bg-gray-neutral active:bg-gray-dark"
                onClick={handleFavoriteClick}
              >
                <FavoriteStar isFavorite={isFavorite} />
              </div>
            )}

            <div className="mr-21 flex shrink-0 items-center">
              <TokenIcon
                symbol={tokenIconName}
                displaySize={32}
                importSize={40}
                badge={tokenIconBadge}
                className="min-h-32 min-w-32"
              />
            </div>
            <div>
              <div className="flex items-center text-16 font-medium leading-[1.5] text-white">
                <span className="inline-block max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap">
                  {primaryLabel}
                </span>

                {marketOrGlv.isDisabled && (
                  <span className="bg-orange text-10 ml-4 mt-8 inline-flex rounded-4 px-4 py-2 font-bold uppercase tabular-nums tracking-wide text-white">
                    <Trans>Disabled</Trans>
                  </span>
                )}
                <div className="inline-block">
                  <GmAssetDropdown token={token} marketsInfoData={marketsInfoData} tokensData={tokensData} />
                </div>
              </div>
              {secondaryLabel && (
                <div className="text-12 font-medium leading-[1.5] tracking-normal text-gray-lighter">
                  {secondaryLabel}
                </div>
              )}
            </div>
          </div>
          {showDebugValues && <span style={tokenAddressStyle}>{marketOrGlvTokenAddress}</span>}
        </div>
      </TableTd>
      <TableTd>
        <AmountWithUsdHuman
          multiline
          amount={totalSupply}
          decimals={token.decimals}
          usd={totalSupplyUsd}
          symbol={token.symbol}
          usdOnTop
        />
      </TableTd>
      <TableTd>
        <GmTokensBalanceInfo
          token={token}
          daysConsidered={daysConsidered}
          earnedRecently={marketEarnings?.recent}
          earnedTotal={marketEarnings?.total}
          isGlv={isGlv}
        />
      </TableTd>

      <TableTd>
        <AprInfo apy={apy} incentiveApr={incentiveApr} lidoApr={lidoApr} marketAddress={token.address} />
      </TableTd>

      <TableTd>
        {performance !== undefined ? (
          <span className="font-medium">{formatPerformanceBps(performance)}</span>
        ) : isGraphLoading ? (
          <Skeleton width={72} baseColor="#B4BBFF1A" highlightColor="#B4BBFF1A" />
        ) : (
          "..."
        )}
      </TableTd>

      <TableTd>
        <SnapshotGraph
          performanceSnapshots={performanceSnapshots ?? EMPTY_ARRAY}
          performance={performance ?? 0}
          isLoading={isGraphLoading}
        />
      </TableTd>

      <TableTd className="!pr-0">
        <Button
          className="flex-grow !px-20 !py-7"
          variant="tertiary"
          to={`/pools/details?market=${marketOrGlvTokenAddress}`}
        >
          <Trans>Pool Details</Trans>
        </Button>
      </TableTd>
    </TableTr>
  );
}

const MOBILE_SNAPSHOT_GRAPH_SIZE = {
  width: 88,
  height: 30,
};

const DESKTOP_SNAPSHOT_GRAPH_SIZE = {
  width: 160,
  height: 32,
};

const MOBILE_GRAPH_SIZE_CLASSNAME = `h-[${MOBILE_SNAPSHOT_GRAPH_SIZE.height}px] w-[${MOBILE_SNAPSHOT_GRAPH_SIZE.width}px]`;
const DESKTOP_GRAPH_SIZE_CLASSNAME = `h-[${DESKTOP_SNAPSHOT_GRAPH_SIZE.height}px] w-[${DESKTOP_SNAPSHOT_GRAPH_SIZE.width}px]`;

const SnapshotGraph = ({
  performanceSnapshots,
  performance,
  isLoading,
}: {
  performanceSnapshots: PerformanceSnapshot[];
  performance: number;
  isLoading?: boolean;
}) => {
  const isNegative = performance < 0;

  const isMobile = usePoolsIsMobilePage();
  const size = isMobile ? MOBILE_SNAPSHOT_GRAPH_SIZE : DESKTOP_SNAPSHOT_GRAPH_SIZE;
  const curveFunction = curveCardinal.tension(0.01);
  const maxPoints = isMobile ? 18 : 24;
  const chartData = useMemo(() => {
    if (!performanceSnapshots || performanceSnapshots.length <= maxPoints) {
      return performanceSnapshots;
    }

    const stride = Math.ceil(performanceSnapshots.length / maxPoints);
    const reduced = performanceSnapshots.filter((_item, index) => index % stride === 0);
    const lastPoint = performanceSnapshots[performanceSnapshots.length - 1];

    if (reduced[reduced.length - 1] !== lastPoint) {
      reduced.push(lastPoint);
    }

    return reduced;
  }, [maxPoints, performanceSnapshots]);

  return (
    <div
      className={cx({
        [MOBILE_GRAPH_SIZE_CLASSNAME]: isMobile,
        [DESKTOP_GRAPH_SIZE_CLASSNAME]: !isMobile,
      })}
    >
      {isLoading ? (
        <Skeleton width={size.width} height={size.height} baseColor="#B4BBFF1A" highlightColor="#B4BBFF1A" />
      ) : (
        <AreaChart width={size.width} height={size.height} data={chartData}>
          <Area
            type={curveFunction}
            dataKey="performance"
            stroke={isNegative ? "var(--color-red-alpha-100)" : "var(--color-primary-alpha-100)"}
            fill={isNegative ? "var(--color-red-alpha-100)" : "var(--color-primary-alpha-100)"}
            fillOpacity={0.08}
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
        </AreaChart>
      )}
    </div>
  );
};
